import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, SCHOOLS } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Edit3, GripVertical, BookOpen, FileQuestion, ChevronDown, ChevronRight, Upload, Sparkles } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';

// LIST   /admin/builder/courses
export function CourseListPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/builder/courses').then(({ data }) => setRows(data)); }, []);

  return (
    <div data-testid="admin-course-list">
      <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold font-semibold">Builders</div>
          <h1 className="font-display font-semibold text-2xl text-navy mt-1">Course Builder</h1>
        </div>
        <Link to="/admin/builder/courses/new" data-testid="new-course-btn"><Button className="btn-gold"><Plus className="h-4 w-4 mr-1" /> New Course</Button></Link>
      </div>
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>{['Title','School','Program','Modules','Actions'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.course_id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{r.title}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.school}</td>
                  <td className="px-4 py-3 text-slate-700">{r.program_slug || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{(r.modules || []).length} modules</td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/builder/courses/${r.course_id}`}><Button size="sm" variant="outline" data-testid={`edit-course-${r.course_id}`}><Edit3 className="h-3.5 w-3.5" /></Button></Link>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-12">No courses yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// EDIT /admin/builder/courses/:id (new or course_id)
export function CourseBuilderPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [course, setCourse] = useState({
    slug: '', title: '', description: '', program_slug: '', school: SCHOOLS[0].name,
    image_url: '', instructor_name: 'TRJ Academy Faculty', modules: [],
  });
  const [programs, setPrograms] = useState([]);
  // drag-and-drop transient state: { type: 'module'|'lesson', mi, li? }
  const [drag, setDrag] = useState(null);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    api.get('/programs').then(({ data }) => setPrograms(data));
    if (isNew) return;
    api.get(`/admin/builder/courses/${id}`).then(({ data }) => {
      setCourse({
        slug: data.slug, title: data.title, description: data.description || '',
        program_slug: data.program_slug || '', school: data.school,
        image_url: data.image_url || '', instructor_name: data.instructor_name || 'TRJ Academy Faculty',
        modules: (data.modules || []).map(m => ({
          ...m,
          lessons: m.lessons || [],
          quiz: m.quiz || null,
        })),
      });
    });
  }, [id, isNew]);

  const save = async () => {
    try {
      if (isNew) {
        const { data } = await api.post('/admin/builder/courses', course);
        toast.success('Course created');
        navigate(`/admin/builder/courses/${data.course_id}`);
      } else {
        await api.put(`/admin/builder/courses/${id}`, course);
        toast.success('Course saved');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const setField = (k, v) => setCourse(c => ({ ...c, [k]: v }));
  const addModule = () => setCourse(c => ({ ...c, modules: [...c.modules, { title: 'New module', order: c.modules.length + 1, lessons: [], quiz: null }] }));
  const removeModule = (mi) => setCourse(c => ({ ...c, modules: c.modules.filter((_, i) => i !== mi) }));
  const updateModule = (mi, patch) => setCourse(c => ({ ...c, modules: c.modules.map((m, i) => i === mi ? { ...m, ...patch } : m) }));
  const addLesson = (mi) => updateModule(mi, { lessons: [...course.modules[mi].lessons, { title: 'New lesson', content_type: 'text', body: '', video_url: '', duration_minutes: 10, order: course.modules[mi].lessons.length + 1 }] });
  const updateLesson = (mi, li, patch) => {
    const lessons = course.modules[mi].lessons.map((l, idx) => idx === li ? { ...l, ...patch } : l);
    updateModule(mi, { lessons });
  };
  const removeLesson = (mi, li) => {
    const lessons = course.modules[mi].lessons.filter((_, idx) => idx !== li);
    updateModule(mi, { lessons });
  };
  const ensureQuiz = (mi) => {
    if (course.modules[mi].quiz) return;
    updateModule(mi, { quiz: { title: 'Module Quiz', pass_threshold: 0.7, questions: [] } });
  };
  const updateQuiz = (mi, patch) => updateModule(mi, { quiz: { ...course.modules[mi].quiz, ...patch } });
  const addQuestion = (mi) => {
    const q = { prompt: 'New question', options: ['Option A', 'Option B', 'Option C', 'Option D'], correct_index: 0 };
    updateQuiz(mi, { questions: [...course.modules[mi].quiz.questions, q] });
  };
  const updateQuestion = (mi, qi, patch) => {
    const questions = course.modules[mi].quiz.questions.map((q, idx) => idx === qi ? { ...q, ...patch } : q);
    updateQuiz(mi, { questions });
  };
  const removeQuestion = (mi, qi) => {
    const questions = course.modules[mi].quiz.questions.filter((_, idx) => idx !== qi);
    updateQuiz(mi, { questions });
  };
  const removeQuiz = (mi) => updateModule(mi, { quiz: null });

  // ------------------- Drag-and-drop reorder -------------------
  const persistOrder = async (modulesNext) => {
    // Update local state first for instant feedback.
    setCourse(c => ({ ...c, modules: modulesNext }));
    if (isNew) return; // no course_id yet — Save will persist on first save
    // Only persist if every module/lesson has an id assigned by the server.
    const allHaveIds = modulesNext.every(
      m => m.module_id && (m.lessons || []).every(l => l.lesson_id)
    );
    if (!allHaveIds) return;
    setReordering(true);
    try {
      await api.post(`/admin/builder/courses/${id}/reorder`, {
        module_ids: modulesNext.map(m => m.module_id),
        lesson_ids_by_module: Object.fromEntries(
          modulesNext.map(m => [m.module_id, m.lessons.map(l => l.lesson_id)])
        ),
      });
      toast.success('Order saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reorder failed');
    } finally {
      setReordering(false);
    }
  };

  const moveModule = (from, to) => {
    if (from === to || to < 0 || to >= course.modules.length) return;
    const next = [...course.modules];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    persistOrder(next.map((m, i) => ({ ...m, order: i + 1 })));
  };

  const moveLesson = (mi, from, to) => {
    const lessons = [...course.modules[mi].lessons];
    if (from === to || to < 0 || to >= lessons.length) return;
    const [item] = lessons.splice(from, 1);
    lessons.splice(to, 0, item);
    const renumbered = lessons.map((l, i) => ({ ...l, order: i + 1 }));
    const next = course.modules.map((m, i) => i === mi ? { ...m, lessons: renumbered } : m);
    persistOrder(next);
  };

  const onModuleDragStart = (mi) => () => setDrag({ type: 'module', mi });
  const onModuleDragOver = (e) => { if (drag?.type === 'module') e.preventDefault(); };
  const onModuleDrop = (toMi) => (e) => {
    e.preventDefault();
    if (drag?.type === 'module' && drag.mi !== toMi) moveModule(drag.mi, toMi);
    setDrag(null);
  };

  const onLessonDragStart = (mi, li) => (e) => {
    e.stopPropagation();
    setDrag({ type: 'lesson', mi, li });
  };
  const onLessonDragOver = (mi) => (e) => {
    if (drag?.type === 'lesson' && drag.mi === mi) e.preventDefault();
  };
  const onLessonDrop = (mi, toLi) => (e) => {
    e.preventDefault();
    if (drag?.type === 'lesson' && drag.mi === mi && drag.li !== toLi) {
      moveLesson(mi, drag.li, toLi);
    }
    setDrag(null);
  };

  return (
    <div data-testid="course-builder">
      <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/builder/courses" className="text-slate-500 hover:text-navy"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">{isNew ? 'New Course' : 'Edit Course'}</div>
            <h1 className="font-display font-semibold text-2xl text-navy mt-1">{course.title || 'Untitled course'}</h1>
          </div>
        </div>
        <Button onClick={save} className="btn-navy" data-testid="save-course-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
      </div>

      <div className="p-8 max-w-6xl space-y-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-navy mb-4">Basics</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Slug"><Input data-testid="cb-slug" value={course.slug} onChange={(e) => setField('slug', e.target.value)} /></Field>
            <Field label="Title"><Input data-testid="cb-title" value={course.title} onChange={(e) => setField('title', e.target.value)} /></Field>
            <Field label="School">
              <Select value={course.school} onValueChange={(v) => setField('school', v)}>
                <SelectTrigger data-testid="cb-school"><SelectValue /></SelectTrigger>
                <SelectContent>{SCHOOLS.map(s => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Program (attach to)">
              <Select value={course.program_slug || '__none__'} onValueChange={(v) => setField('program_slug', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="cb-program"><SelectValue placeholder="(unattached)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(unattached)</SelectItem>
                  {programs.map(p => <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Image URL" className="md:col-span-2"><Input data-testid="cb-image" value={course.image_url} onChange={(e) => setField('image_url', e.target.value)} /></Field>
            <Field label="Instructor"><Input data-testid="cb-instructor" value={course.instructor_name} onChange={(e) => setField('instructor_name', e.target.value)} /></Field>
            <Field label="Description" className="md:col-span-2"><Textarea rows={3} data-testid="cb-desc" value={course.description} onChange={(e) => setField('description', e.target.value)} /></Field>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-navy">Modules & Lessons</h3>
              <p className="text-xs text-slate-500 mt-1">Drag the <GripVertical className="inline h-3 w-3 -mt-0.5" /> handle to reorder modules or lessons{reordering ? ' · saving…' : ''}.</p>
            </div>
            <Button size="sm" className="btn-gold" onClick={addModule} data-testid="add-module-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Module</Button>
          </div>

          {course.modules.length === 0 && <div className="text-sm text-slate-500">No modules yet. Add one to begin.</div>}

          <Accordion type="multiple" className="space-y-3">
            {course.modules.map((m, mi) => (
              <AccordionItem
                key={m.module_id || mi}
                value={`m${mi}`}
                className={`border border-slate-200 rounded-lg overflow-hidden ${drag?.type === 'module' && drag.mi !== mi ? 'ring-1 ring-gold/40' : ''}`}
                data-testid={`module-${mi}`}
                onDragOver={onModuleDragOver}
                onDrop={onModuleDrop(mi)}
              >
                <div className="flex items-center bg-slate-50">
                  <span
                    className="cursor-grab active:cursor-grabbing px-3 py-3 text-slate-400 hover:text-navy"
                    draggable
                    onDragStart={onModuleDragStart(mi)}
                    onDragEnd={() => setDrag(null)}
                    data-testid={`drag-module-${mi}`}
                    title="Drag to reorder module"
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>
                  <AccordionTrigger className="flex-1 px-4 hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <BookOpen className="h-4 w-4 text-navy" />
                      <div>
                        <div className="font-display font-semibold text-navy">{m.title}</div>
                        <div className="text-xs text-slate-500">{m.lessons.length} lessons · {m.quiz ? 'quiz included' : 'no quiz'}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <Button size="sm" variant="outline" className="mr-3" onClick={() => removeModule(mi)} data-testid={`del-module-${mi}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <AccordionContent className="p-4 space-y-4 bg-white">
                  <div className="grid md:grid-cols-3 gap-3">
                    <Field className="md:col-span-2" label="Module title"><Input value={m.title} onChange={(e) => updateModule(mi, { title: e.target.value })} data-testid={`mod-title-${mi}`} /></Field>
                    <Field label="Order"><Input type="number" value={m.order} onChange={(e) => updateModule(mi, { order: parseInt(e.target.value) || 0 })} /></Field>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mt-2">
                      <h4 className="text-sm font-display font-semibold text-navy uppercase tracking-widest">Lessons</h4>
                      <Button size="sm" variant="outline" onClick={() => addLesson(mi)} data-testid={`add-lesson-${mi}`}><Plus className="h-3 w-3 mr-1" /> Add Lesson</Button>
                    </div>
                    <div className="mt-3 space-y-3">
                      {m.lessons.map((l, li) => (
                        <div
                          key={l.lesson_id || li}
                          className={`border border-slate-200 rounded-md p-4 flex gap-3 ${drag?.type === 'lesson' && drag.mi === mi && drag.li !== li ? 'ring-1 ring-gold/40' : ''}`}
                          data-testid={`lesson-${mi}-${li}`}
                          onDragOver={onLessonDragOver(mi)}
                          onDrop={onLessonDrop(mi, li)}
                        >
                          <span
                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-navy mt-1"
                            draggable
                            onDragStart={onLessonDragStart(mi, li)}
                            onDragEnd={() => setDrag(null)}
                            data-testid={`drag-lesson-${mi}-${li}`}
                            title="Drag to reorder lesson"
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                          <div className="flex-1">
                          <div className="grid md:grid-cols-12 gap-3">
                            <Field className="md:col-span-6" label="Title"><Input value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} /></Field>
                            <Field className="md:col-span-3" label="Type">
                              <Select value={l.content_type} onValueChange={(v) => updateLesson(mi, li, { content_type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="video">Video</SelectItem>
                                  <SelectItem value="pdf">PDF</SelectItem>
                                </SelectContent>
                              </Select>
                            </Field>
                            <Field className="md:col-span-2" label="Minutes"><Input type="number" value={l.duration_minutes} onChange={(e) => updateLesson(mi, li, { duration_minutes: parseInt(e.target.value) || 0 })} /></Field>
                            <div className="md:col-span-1 flex items-end"><Button size="sm" variant="outline" onClick={() => removeLesson(mi, li)}><Trash2 className="h-3.5 w-3.5" /></Button></div>
                          </div>
                          {l.content_type === 'video' && (
                            <div className="mt-2"><Field label="Video URL (YouTube/Vimeo embed)"><Input value={l.video_url || ''} onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })} placeholder="https://www.youtube.com/embed/…" /></Field></div>
                          )}
                          <div className="mt-2"><Field label="Body / Notes"><Textarea rows={3} value={l.body || ''} onChange={(e) => updateLesson(mi, li, { body: e.target.value })} /></Field></div>
                          </div>
                        </div>
                      ))}
                      {m.lessons.length === 0 && <div className="text-xs text-slate-500">No lessons yet.</div>}
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-display font-semibold text-navy uppercase tracking-widest flex items-center gap-2"><FileQuestion className="h-4 w-4 text-gold" /> Quiz</h4>
                      {!m.quiz
                        ? <Button size="sm" className="btn-gold" onClick={() => ensureQuiz(mi)} data-testid={`add-quiz-${mi}`}><Plus className="h-3 w-3 mr-1" /> Add Quiz</Button>
                        : <Button size="sm" variant="outline" onClick={() => removeQuiz(mi)}><Trash2 className="h-3.5 w-3.5" /> Remove Quiz</Button>}
                    </div>
                    {m.quiz && (
                      <div className="mt-3 space-y-3">
                        <div className="grid md:grid-cols-3 gap-3">
                          <Field className="md:col-span-2" label="Quiz title"><Input value={m.quiz.title} onChange={(e) => updateQuiz(mi, { title: e.target.value })} data-testid={`quiz-title-${mi}`} /></Field>
                          <Field label="Pass threshold (0–1)"><Input type="number" step="0.05" min="0" max="1" value={m.quiz.pass_threshold} onChange={(e) => updateQuiz(mi, { pass_threshold: parseFloat(e.target.value) || 0.7 })} /></Field>
                        </div>
                        <div className="space-y-3">
                          {m.quiz.questions.map((q, qi) => (
                            <div key={qi} className="border border-slate-200 rounded-md p-4" data-testid={`question-${mi}-${qi}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-xs uppercase tracking-widest text-gold font-semibold">Question {qi + 1}</div>
                                <Button size="sm" variant="outline" onClick={() => removeQuestion(mi, qi)}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                              <Field label="Prompt"><Textarea rows={2} value={q.prompt} onChange={(e) => updateQuestion(mi, qi, { prompt: e.target.value })} /></Field>
                              <div className="mt-3 space-y-2">
                                {q.options.map((opt, oi) => (
                                  <div key={oi} className="flex items-center gap-2">
                                    <input type="radio" name={`correct-${mi}-${qi}`} checked={q.correct_index === oi} onChange={() => updateQuestion(mi, qi, { correct_index: oi })} className="accent-navy" />
                                    <Input value={opt} onChange={(e) => {
                                      const options = [...q.options]; options[oi] = e.target.value;
                                      updateQuestion(mi, qi, { options });
                                    }} placeholder={`Option ${String.fromCharCode(65 + oi)}`} />
                                    <Button size="sm" variant="outline" onClick={() => {
                                      const options = q.options.filter((_, idx) => idx !== oi);
                                      const ci = q.correct_index >= oi && q.correct_index > 0 ? q.correct_index - 1 : q.correct_index;
                                      updateQuestion(mi, qi, { options, correct_index: ci });
                                    }}><Trash2 className="h-3 w-3" /></Button>
                                  </div>
                                ))}
                                <Button size="sm" variant="outline" onClick={() => updateQuestion(mi, qi, { options: [...q.options, ''] })} data-testid={`add-option-${mi}-${qi}`}><Plus className="h-3 w-3 mr-1" /> Add Option</Button>
                              </div>
                            </div>
                          ))}
                          <Button size="sm" className="btn-gold" onClick={() => addQuestion(mi)} data-testid={`add-question-${mi}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add Question</Button>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return <div className={className}><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}

function BulkQuestionsDialog({ courseId, quizId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);

  if (!courseId || courseId === 'new') return null;

  const parse = async () => {
    if (!csv.trim()) return;
    const { data } = await api.post('/admin/builder/quiz/parse-csv', { csv });
    setParsed(data.questions); setErrors(data.errors || []);
  };
  const commit = async () => {
    if (parsed.length === 0) { toast.error('Parse first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/builder/courses/${courseId}/quizzes/${quizId}/questions/bulk`,
        { questions: parsed, replace },
      );
      toast.success(`${data.added} question(s) added (${data.total} total)`);
      setOpen(false); setCsv(''); setParsed([]); setErrors([]);
      onAdded && onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk add failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="bulk-add-questions-btn"><Sparkles className="h-3.5 w-3.5 mr-1" /> Bulk Add</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Bulk Add Questions</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600">
            Paste questions, one per line. Format (pipe-separated, last column is the 0-based correct option index):
            <pre className="bg-slate-100 rounded p-2 text-xs mt-2 overflow-x-auto">Prompt text|Option A|Option B|Option C|Option D|1</pre>
            <span className="text-xs">Also accepts CSV (commas) or TSV (tabs). 2–6 options per question.</span>
          </div>
          <Textarea rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="What is Form 1040?|A tax return form|An income statement|A 1099 form|0&#10;Filing status affects:|Brackets and standard deduction|Nothing|Refund amount|0"
            className="font-mono text-xs" data-testid="bulk-csv-input" />
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={parse} data-testid="bulk-parse-btn">Preview</Button>
            <label className="text-xs flex items-center gap-2 ml-auto text-slate-600">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-navy" data-testid="bulk-replace-toggle" />
              Replace existing questions instead of appending
            </label>
          </div>
          {errors.length > 0 && (
            <div className="bg-red-50 text-red-700 rounded p-3 text-xs">
              {errors.map((e, i) => <div key={i}>Line {e.line}: {e.error}</div>)}
            </div>
          )}
          {parsed.length > 0 && (
            <div className="border border-slate-200 rounded-md p-3 max-h-72 overflow-y-auto" data-testid="bulk-preview">
              <div className="text-xs uppercase tracking-widest text-trjgreen font-semibold mb-2">{parsed.length} valid question(s)</div>
              <ol className="space-y-2 text-sm list-decimal pl-5">
                {parsed.map((q, i) => (
                  <li key={i}>
                    <div className="font-semibold text-navy">{q.prompt}</div>
                    <ul className="text-xs text-slate-600 ml-2 mt-1">
                      {q.options.map((opt, oi) => (
                        <li key={oi} className={oi === q.correct_index ? 'text-trjgreen font-semibold' : ''}>{String.fromCharCode(65 + oi)}. {opt}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="btn-navy" disabled={busy || parsed.length === 0} onClick={commit} data-testid="bulk-commit-btn">
            <Upload className="h-4 w-4 mr-2" /> {busy ? 'Adding…' : `Add ${parsed.length} question(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
